import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, ImageOff } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTableColumnHeader } from '@/shared/components/data-table/data-table-column-header';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';

import { BrandRowActions } from './brand-row-actions';
import { attentionLabel } from '@/shared/lib/catalogue-labels';
import type { BrandListItem, BrandSellability } from '../types/brand.types';

/**
 * Column definitions for the brands table.
 *
 * Kept in their own file because column defs grow — cell renderers, widths,
 * sort flags, meta — and mixing them into the page makes both harder to read
 * (§91).
 *
 * Sortable column ids are the list's sort keys (`name`, `sortOrder`,
 * `denominationCount`), which `brands.api.ts` maps to the Admin API's
 * whitelist; every other column is not sortable, because the server cannot
 * sort by it.
 */
export function createBrandColumns({
  countryLabel,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  /** The selected Admin country, for the "In <country>" column header. */
  countryLabel: string | null;
  onEdit: (brand: BrandListItem) => void;
  onToggleActive: (brand: BrandListItem) => void;
  onDelete: (brand: BrandListItem) => void;
}): ColumnDef<BrandListItem, unknown>[] {
  return [
    {
      id: 'logo',
      header: '',
      size: 48,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => <BrandLogo brand={row.original} />,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Brand" />,
      enableHiding: false,
      meta: { label: 'Brand' },
      cell: ({ row }) => {
        const brand = row.original;
        return (
          <div className="min-w-0">
            {/* The name is the row's identity, so it is the click target for
                the detail view — a clickable value gets brand colour (§43). */}
            <Link
              to={`/brands/${brand.id}`}
              className="text-primary hover:text-primary/80 block truncate font-medium hover:underline"
            >
              {brand.name}
            </Link>
            {brand.arName && (
              <span className="text-muted-foreground block truncate text-[11px]" dir="rtl">
                {brand.arName}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'category',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
      enableSorting: false,
      meta: { label: 'Category' },
      cell: ({ row }) =>
        row.original.category.nameEn ?? <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: 'isActive',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      enableSorting: false,
      size: 110,
      meta: { label: 'Status' },
      cell: ({ row }) => (
        <StatusBadge variant={row.original.isActive ? 'success' : 'neutral'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
    },
    {
      accessorKey: 'denominationCount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Denominations" align="end" />
      ),
      size: 120,
      meta: { label: 'Denominations' },
      cell: ({ row }) => (
        <div className="text-end tabular-nums">
          {row.original.activeDenominationCount}
          <span className="text-muted-foreground"> / {row.original.denominationCount} active</span>
        </div>
      ),
    },
    {
      id: 'country',
      header: countryLabel ? `In ${countryLabel}` : 'Country',
      enableSorting: false,
      size: 150,
      meta: { label: 'Country availability' },
      cell: ({ row }) => <CountrySummaryCell brand={row.original} />,
    },
    {
      id: 'attention',
      header: 'Attention',
      enableSorting: false,
      meta: { label: 'Attention' },
      cell: ({ row }) => <AttentionCell codes={row.original.attention} />,
    },
    {
      accessorKey: 'sortOrder',
      // Position 1 first: TanStack would start a numeric column descending.
      sortDescFirst: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Storefront position" align="end" />
      ),
      size: 120,
      meta: { label: 'Storefront position' },
      cell: ({ row }) => (
        <div className="text-end tabular-nums">{row.original.sortOrder ?? '—'}</div>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 48,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <BrandRowActions
          brand={row.original}
          onEdit={onEdit}
          onToggleActive={onToggleActive}
          onDelete={onDelete}
        />
      ),
    },
  ];
}

const SELLABILITY: Record<BrandSellability, { variant: StatusVariant; label: string }> = {
  All: { variant: 'success', label: 'All purchasable' },
  Some: { variant: 'warning', label: 'Partly purchasable' },
  None: { variant: 'danger', label: 'Not purchasable' },
};

function CountrySummaryCell({ brand }: { brand: BrandListItem }) {
  const summary = brand.countrySummary;
  if (!summary) return <span className="text-muted-foreground">—</span>;

  if (summary.offered === 0) {
    return <StatusBadge variant="neutral">Not offered</StatusBadge>;
  }

  const sellability = SELLABILITY[summary.sellability];
  return (
    <div className="space-y-0.5">
      <StatusBadge variant={sellability.variant}>{sellability.label}</StatusBadge>
      <p className="text-muted-foreground text-[11px] tabular-nums">
        {summary.sellable} of {summary.offered} offered can be bought
      </p>
    </div>
  );
}

function AttentionCell({ codes }: { codes: string[] }) {
  if (codes.length === 0) return <span className="text-muted-foreground">—</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="text-status-warning inline-flex items-center gap-1 text-[11px] font-medium"
        >
          <AlertTriangle className="size-3.5" aria-hidden />
          {codes.length === 1 ? attentionLabel(codes.join()) : `${codes.length} issues`}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <ul className="space-y-0.5 text-xs">
          {codes.map((code) => (
            <li key={code}>{attentionLabel(code)}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

function BrandLogo({ brand }: { brand: BrandListItem }) {
  if (!brand.imageUrl) {
    return (
      <div className="bg-muted text-muted-foreground flex size-8 items-center justify-center rounded">
        <ImageOff className="size-3.5" aria-hidden />
      </div>
    );
  }

  return (
    <img
      src={brand.imageUrl}
      alt=""
      className="bg-muted size-8 rounded object-contain"
      loading="lazy"
      // Brand images are supplied by operators and the URLs go stale; a broken
      // image icon in every row of a table looks like the admin is broken.
      onError={(event) => {
        event.currentTarget.style.visibility = 'hidden';
      }}
    />
  );
}
